import { ApiError } from "@nabble/sdk";
import { useToast } from "@nabble/ui";
import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchPolicies, savePolicies, type Policies } from "../../../api/adminPolicies";
import { errorMessage } from "../../../errors";
import { type AllowedRange, changedKeys, invalidFields, type PolicyValues, rangesFromViolations, toPolicies, toValues } from "./policyValues";

export interface PoliciesForm {
  saved: Policies | null;
  defaults: Policies | null;
  values: PolicyValues | null;
  setValue: (key: keyof Policies, value: string) => void;
  loadError: string | null;
  load: () => void;
  dirtyKeys: (keyof Policies)[];
  invalid: Record<string, string>;
  ranges: Record<string, AllowedRange>;
  saving: boolean;
  save: () => Promise<void>;
  discard: () => void;
}

/** Loads the security policies, tracks the edits and saves them, explaining any value below its floor. */
export function usePolicies(): PoliciesForm {
  const { show } = useToast();
  const [saved, setSaved] = useState<Policies | null>(null);
  const [values, setValues] = useState<PolicyValues | null>(null);
  const [defaults, setDefaults] = useState<Policies | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [ranges, setRanges] = useState<Record<string, AllowedRange>>({});

  const load = useCallback(() => {
    setLoadError(null);
    fetchPolicies()
      .then((data) => {
        setSaved(data.current);
        setValues(toValues(data.current));
        setDefaults(data.defaults);
      })
      .catch((err: unknown) => setLoadError(errorMessage(err, "We couldn't load the policies.")));
  }, []);

  useEffect(load, [load]);

  const dirtyKeys = useMemo(() => (values && saved ? changedKeys(values, saved) : []), [values, saved]);
  const invalid = useMemo(() => (values ? invalidFields(values) : {}), [values]);

  async function save(): Promise<void> {
    if (!values || Object.keys(invalid).length > 0) return;
    setSaving(true);
    setRanges({});
    const payload = toPolicies(values);
    try {
      await savePolicies(payload);
      setSaved(payload);
      setValues(toValues(payload));
      show({ title: "Policies saved", text: "The change was recorded in the audit trail.", tone: "success" });
    } catch (err) {
      if (err instanceof ApiError && err.code === "below_security_floor" && err.body.violations) {
        setRanges(rangesFromViolations(err.body.violations));
        show({ title: "Check the highlighted values", text: "Some are outside the allowed security range.", tone: "danger" });
      } else {
        show({ title: "We couldn't save the policies", text: errorMessage(err, "Please try again."), tone: "danger" });
      }
    } finally {
      setSaving(false);
    }
  }

  function setValue(key: keyof Policies, value: string): void {
    if (values) setValues({ ...values, [key]: value });
  }

  function discard(): void {
    if (saved) setValues(toValues(saved));
    setRanges({});
  }

  return { saved, defaults, values, setValue, loadError, load, dirtyKeys, invalid, ranges, saving, save, discard };
}
